/* eslint-disable @typescript-eslint/no-explicit-any */
import { Conditions } from '@vsolv/packages/conditions/domain';
import { PropertySet } from '@vsolv/packages/properties/domain';
import { Policy } from '../../../policy';
import { mergeDimensionValues, normalizePropertyValues } from '../../../utils/property-values.utils';
import { Warranty } from '../../../warranty';
import { UsageAtLoss } from '../../intake';
import { LiabilityInfo } from '../liability-info';
import { Finding, Inputs, Limits, UsageCheck } from './model';
import { Code, Outcome, OUTCOME_RANK, RULES_VERSION, Step, STEP_ORDER } from './outcome';

/** Everything the evaluator needs, loaded server-side. */
export interface Context {
  timeZone: string;

  lossDate: Date | null;
  claimDate: Date | null;

  warranty: Pick<
    Warranty.Model,
    | 'termStart'
    | 'expiryDate'
    | 'cancellationDate'
    | 'status'
    | 'frozen'
    | 'renewalCount'
    | 'policyId'
    | 'planId'
    | 'termId'
    | 'coverages'
  >;
  policy: Pick<Policy.Model, 'propertySetId' | 'claimWaitingPeriod'>;
  plan: Pick<
    Policy.Plan.Model,
    'conditions' | 'termConditions' | 'termUsageLimits' | 'coverageConfigs' | 'liabilityPerClaim'
  > | null;

  /** The policy's asset type, for required values. */
  propertySet: Pick<PropertySet.Model, 'properties' | 'dimensions'> | null;
  submission: Pick<PropertySet.Submission, 'id' | 'setId' | 'value' | 'dimensionValues'> | null;

  /** What conditions are checked against; see `buildPropertyValues`. */
  propertyValues: Record<string, any>;
  assetValuesAsOfYear: number;

  coverageId: string | null;
  usageAtLoss: UsageAtLoss | null;

  /** Cents across the job's active charges. */
  requestedAmount: number;

  /** From the existing liability calculation; null when the coverage isn't on the warranty. */
  liability: LiabilityInfo | null;
}

export interface Evaluation {
  outcome: Outcome;
  rulesVersion: number;
  inputs: Inputs;
  findings: Finding[];
  usage: UsageCheck[];
  limits: Limits | null;
}

/** The same property bag checkout evaluates plans against, with ages taken as of `asOfYear`. */
export function buildPropertyValues(args: {
  submission: Pick<PropertySet.Submission, 'value' | 'dimensionValues'> | null;
  asOfYear: number;
  customer?: unknown;
  distributor?: unknown;
  shippingAddress?: unknown;
}): Record<string, any> {
  return mergeDimensionValues(
    normalizePropertyValues(
      {
        ...(args.submission?.value ?? {}),
        customer: args.customer ?? null,
        distributor: args.distributor ?? null,
        shippingAddress: args.shippingAddress ?? null,
      },
      args.asOfYear
    ),
    args.submission?.dimensionValues
  );
}

/** Calendar day (YYYY-MM-DD) of a moment in the given zone. */
export function toCalendarDay(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const part = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  return value instanceof Date ? value : new Date(value);
}

function isValid(date: Date | null): date is Date {
  return !!date && !isNaN(date.getTime());
}

function isMissing(value: unknown): boolean {
  return PropertySet.isMissingValue(value) || (Array.isArray(value) && value.length === 0);
}

// Three-valued condition result: a failed check on a missing value can't decide anything.
type Truth = 'TRUE' | 'FALSE' | 'UNKNOWN';

function truthOf(result: Conditions.ConditionEvaluationResult): Truth {
  if (result.kind === 'condition') {
    if (result.passed) return 'TRUE';
    return isMissing(result.actualValue) ? 'UNKNOWN' : 'FALSE';
  }

  const truths = result.conditions.map(truthOf);
  if (result.comparison === Conditions.Comparison.AND) {
    if (truths.includes('FALSE')) return 'FALSE';
    return truths.includes('UNKNOWN') ? 'UNKNOWN' : 'TRUE';
  }

  if (truths.includes('TRUE')) return 'TRUE';
  return truths.includes('UNKNOWN') ? 'UNKNOWN' : 'FALSE';
}

function leaves(result: Conditions.ConditionEvaluationResult): Conditions.EvaluatedConditionResult[] {
  return result.kind === 'condition' ? [result] : result.conditions.flatMap(leaves);
}

function conditionFinding(
  step: Step,
  code: Code,
  outcome: Outcome,
  leaf: Conditions.EvaluatedConditionResult,
  message: string
): Finding {
  return {
    step,
    code,
    outcome,
    message,
    path: leaf.propertyId,
    label: leaf.propertyLabel,
    operator: leaf.operator,
    expected: leaf.expectedValue,
    actual: leaf.actualValue ?? null,
  };
}

/** Plan or term conditions: failures on present values exclude, missing values ask for more information. */
function conditionGroupFindings(
  group: Conditions.ConditionGroup | null | undefined,
  values: Record<string, any>,
  failedCode: Code.PLAN_CONDITION_FAILED | Code.TERM_CONDITION_FAILED,
  label: string
): Finding[] {
  if (!group?.conditions?.length) return [];

  const result = Conditions.evaluateConditionGroupDetailed(group, values);
  const truth = truthOf(result);
  if (truth === 'TRUE') return [];

  const failed = leaves(result).filter(leaf => !leaf.passed && !leaf.skipped);
  if (truth === 'UNKNOWN') {
    return failed
      .filter(leaf => isMissing(leaf.actualValue))
      .map(leaf =>
        conditionFinding(
          Step.ASSET,
          Code.ASSET_VALUE_MISSING,
          Outcome.INSUFFICIENT_INFORMATION,
          leaf,
          `${leaf.propertyLabel} is needed to check the ${label}`
        )
      );
  }

  return failed
    .filter(leaf => !isMissing(leaf.actualValue))
    .map(leaf =>
      conditionFinding(Step.ASSET, failedCode, Outcome.EXCLUDED, leaf, `The ${label} require ${leaf.propertyLabel}`)
    );
}

function blockValue<T>(block: { value: T; valuePropertyPath?: string | null }, values: Record<string, any>): T {
  return block.valuePropertyPath ? (Conditions.getPropertyValue(block.valuePropertyPath, values) as T) : block.value;
}

/** Like `Conditions.evaluateRule`, but notices when a block with missing values could change the answer. */
function requirementFindings(
  rule: Conditions.Rule<Policy.Plan.CoverageRequirement>,
  values: Record<string, any>
): Finding[] {
  const NOT_APPLICABLE = Policy.Plan.CoverageRequirementValue.NOT_APPLICABLE;
  const unknown: { value: Policy.Plan.CoverageRequirement; result: Conditions.ConditionEvaluationResult }[] = [];

  let value: Policy.Plan.CoverageRequirement | undefined;
  let matched: Conditions.ConditionEvaluationResult | null = null;
  const unmatched: Conditions.ConditionEvaluationResult[] = [];

  for (const block of rule.blocks ?? []) {
    const result = Conditions.evaluateConditionGroupDetailed(block.condition, values);
    const truth = truthOf(result);

    if (truth === 'TRUE') {
      value = blockValue(block, values);
      matched = result;
      break;
    }

    unmatched.push(result);
    if (truth === 'UNKNOWN') unknown.push({ value: blockValue(block, values), result });
  }

  if (value === undefined)
    value = blockValue({ value: rule.defaultValue, valuePropertyPath: rule.valuePropertyPath }, values);

  const applies = value !== NOT_APPLICABLE;
  const undecided = unknown.filter(block => (block.value !== NOT_APPLICABLE) !== applies);

  if (undecided.length) {
    return undecided
      .flatMap(block => leaves(block.result))
      .filter(leaf => !leaf.passed && isMissing(leaf.actualValue))
      .map(leaf =>
        conditionFinding(
          Step.COVERAGE,
          Code.COVERAGE_VALUE_MISSING,
          Outcome.INSUFFICIENT_INFORMATION,
          leaf,
          `${leaf.propertyLabel} is needed to tell whether the coverage applies`
        )
      );
  }

  if (applies) return [];

  // Explain with the conditions that excluded it, or the ones that would have included it.
  const explaining = matched
    ? leaves(matched).filter(leaf => leaf.passed)
    : unmatched.flatMap(leaves).filter(leaf => !leaf.passed && !leaf.skipped);

  if (!explaining.length) {
    return [
      {
        step: Step.COVERAGE,
        code: Code.COVERAGE_NOT_APPLICABLE,
        outcome: Outcome.EXCLUDED,
        message: "The plan doesn't offer this coverage for this asset",
        expected: 'applicable',
        actual: value,
      },
    ];
  }

  return explaining.map(leaf =>
    conditionFinding(
      Step.COVERAGE,
      Code.COVERAGE_NOT_APPLICABLE,
      Outcome.EXCLUDED,
      leaf,
      `The coverage doesn't apply for this ${leaf.propertyLabel}`
    )
  );
}

/** Only real numbers and numeric text count; blanks, booleans and lists don't. */
function numberOrNull(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;

  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

/** The first block whose conditions hold, else the default; a block with missing values leaves it undecided. */
function usageLimitFor(
  rule: Conditions.Rule<number> | null | undefined,
  values: Record<string, any>,
  labelOf: (path: string) => string | null
): { limit: number | null; findings: Finding[] } {
  let chosen: { value: number | undefined; valuePropertyPath?: string | null } = {
    value: rule?.defaultValue,
    valuePropertyPath: rule?.valuePropertyPath,
  };

  for (const block of rule?.blocks ?? []) {
    const result = Conditions.evaluateConditionGroupDetailed(block.condition, values);
    const truth = truthOf(result);

    if (truth === 'UNKNOWN') {
      const findings = leaves(result)
        .filter(leaf => !leaf.passed && isMissing(leaf.actualValue))
        .map(leaf =>
          conditionFinding(
            Step.USAGE,
            Code.USAGE_LIMIT_UNDECIDED,
            Outcome.INSUFFICIENT_INFORMATION,
            leaf,
            `${leaf.propertyLabel} is needed to tell the usage limit`
          )
        );
      return { limit: null, findings };
    }

    if (truth === 'TRUE') {
      chosen = block;
      break;
    }
  }

  const limit = numberOrNull(blockValue(chosen, values));
  if (limit !== null) return { limit, findings: [] };

  const path = chosen.valuePropertyPath ?? null;
  const label = path ? (labelOf(path) ?? path) : null;
  return {
    limit: null,
    findings: [
      {
        step: Step.USAGE,
        code: Code.USAGE_LIMIT_UNDECIDED,
        outcome: Outcome.INSUFFICIENT_INFORMATION,
        message: label ? `${label} is needed to tell the usage limit` : 'The usage limit has no value',
        path,
        label,
      },
    ],
  };
}

/** Use since the sale (reading at loss − the asset's reading at sale) against the term's limit. */
function usageLimitCheck(
  usageLimit: Policy.Plan.UsageLimit | null | undefined,
  ctx: Context
): { usage: UsageCheck[]; findings: Finding[] } {
  if (!usageLimit) return { usage: [], findings: [] };

  const path = `dimension.${usageLimit.dimensionId}`;
  const readingAtLoss = ctx.usageAtLoss?.dimensionId === usageLimit.dimensionId ? ctx.usageAtLoss.value : null;
  const readingAtSale = numberOrNull(Conditions.getPropertyValue(path, ctx.propertyValues));
  // A plain asset field gets its name here; dimension paths are named by the API.
  const labelOf = (fieldPath: string) =>
    ctx.propertySet?.properties?.find(
      assignment => assignment.property?.valueKey === fieldPath || assignment.propertyId === fieldPath
    )?.property?.name ?? null;
  const { limit, findings } = usageLimitFor(usageLimit.limit, ctx.propertyValues, labelOf);

  const check: UsageCheck = {
    dimensionId: usageLimit.dimensionId,
    label: path,
    readingAtSale,
    readingAtLoss: numberOrNull(readingAtLoss),
    used: null,
    limit,
    passed: null,
  };

  if (check.readingAtLoss === null) {
    findings.push({
      step: Step.USAGE,
      code: Code.USAGE_READING_MISSING,
      outcome: Outcome.INSUFFICIENT_INFORMATION,
      message: `No ${path} reading at the time of loss`,
      path,
    });
  }

  if (readingAtSale === null) {
    findings.push({
      step: Step.USAGE,
      code: Code.USAGE_SALE_READING_MISSING,
      outcome: Outcome.INSUFFICIENT_INFORMATION,
      message: `No ${path} reading from the sale to measure use from`,
      path,
    });
  }

  if (check.readingAtLoss !== null && readingAtSale !== null) {
    // Rounded so decimal readings right at the limit don't tip over it.
    check.used = Math.round((check.readingAtLoss - readingAtSale) * 1e6) / 1e6;

    if (check.used < 0) {
      // A rolled back or replaced meter: someone has to confirm the readings.
      findings.push({
        step: Step.USAGE,
        code: Code.USAGE_READING_BELOW_SALE,
        outcome: Outcome.INSUFFICIENT_INFORMATION,
        message: `The ${path} reading at loss is below the reading at sale`,
        path,
        operator: 'at least',
        expected: readingAtSale,
        actual: check.readingAtLoss,
      });
    } else if (limit !== null && check.used > limit && ctx.warranty.renewalCount > 0) {
      // No reading is kept at renewal. Within the limit since the sale is safe; over it may be miles from before.
      findings.push({
        step: Step.USAGE,
        code: Code.USAGE_RENEWED_UNMEASURED,
        outcome: Outcome.INSUFFICIENT_INFORMATION,
        message: `The warranty was renewed and its ${path} reading at renewal isn't recorded, so someone needs to check the use in this term`,
        path,
        operator: 'at most',
        expected: limit,
        actual: check.used,
      });
    } else if (limit !== null) {
      check.passed = check.used <= limit;

      if (!check.passed) {
        findings.push({
          step: Step.USAGE,
          code: Code.USAGE_LIMIT_EXCEEDED,
          outcome: Outcome.INELIGIBLE,
          message: `Coverage ended: ${path} use since the sale is over the term's limit`,
          path,
          operator: 'at most',
          expected: limit,
          actual: check.used,
        });
      }
    }
  }

  return { usage: [check], findings };
}

/** What's left on the job's coverage, from the existing liability calculation. */
export function limitsOf(ctx: Pick<Context, 'liability' | 'plan' | 'requestedAmount'>): Limits | null {
  const liability = ctx.liability;
  if (!liability) return null;

  const perClaim = !!ctx.plan?.liabilityPerClaim;
  const remainingCoverageLimit = Math.max(0, liability.remainingCoverageItemLimit);

  // Same reading as the adjudicate and resolve screens: a per-claim limit isn't used up by earlier claims.
  const remainingLimit = perClaim ? Math.max(0, liability.limit) : Math.max(0, liability.remainingLiabilityLimit);
  const bindingLimit = !perClaim && liability.remainingLiabilityLimit < remainingCoverageLimit ? 'GROUP' : 'COVERAGE';

  return {
    deductible: liability.deductible,
    coverageLimit: liability.limit,
    priorResolvedAmount: liability.limit - liability.remainingCoverageItemLimit,
    remainingCoverageLimit,
    groups: (liability.groups ?? []).map(group => ({
      id: group.id,
      title: group.title,
      limit: group.limit ?? 0,
      remaining: group.remainingGroupLimit,
    })),
    remainingLimit,
    bindingLimit,
    perClaim,
    requestedAmount: ctx.requestedAmount,
  };
}

/** Runs every check and picks the outcome by step order. Pure: same context, same result. */
export function evaluate(ctx: Context): Evaluation {
  const tz = ctx.timeZone;
  const findings: Finding[] = [];
  const add = (finding: Finding) => findings.push(finding);

  const lossDate = toDate(ctx.lossDate);
  const claimDate = toDate(ctx.claimDate);
  const termStart = toDate(ctx.warranty.termStart) as Date;
  const expiryDate = toDate(ctx.warranty.expiryDate);
  const cancellationDate = toDate(ctx.warranty.cancellationDate);

  const lossDay = isValid(lossDate) ? toCalendarDay(lossDate, tz) : null;
  const claimDay = isValid(claimDate) ? toCalendarDay(claimDate, tz) : null;
  const termStartDay = toCalendarDay(termStart, tz);
  const expiryDay = isValid(expiryDate) ? toCalendarDay(expiryDate, tz) : null;
  const cancellationDay = isValid(cancellationDate) ? toCalendarDay(cancellationDate, tz) : null;

  // 1. Dates
  if (!lossDate) {
    add({
      step: Step.DATES,
      code: Code.LOSS_DATE_MISSING,
      outcome: Outcome.INSUFFICIENT_INFORMATION,
      message: 'The date of loss is needed',
      path: 'lossDate',
    });
  } else if (!lossDay) {
    add({
      step: Step.DATES,
      code: Code.LOSS_DATE_INVALID,
      outcome: Outcome.INSUFFICIENT_INFORMATION,
      message: 'The date of loss is not a valid date',
      path: 'lossDate',
      actual: String(ctx.lossDate),
    });
  }

  if (!claimDay) {
    add({
      step: Step.DATES,
      code: Code.CLAIM_DATE_INVALID,
      outcome: Outcome.INSUFFICIENT_INFORMATION,
      message: 'The claim date is not a valid date',
      path: 'claimDate',
      actual: ctx.claimDate ? String(ctx.claimDate) : null,
    });
  } else if (lossDay && lossDay > claimDay) {
    add({
      step: Step.DATES,
      code: Code.LOSS_AFTER_CLAIM_DATE,
      outcome: Outcome.INSUFFICIENT_INFORMATION,
      message: 'The date of loss is after the claim date',
      path: 'lossDate',
      operator: 'on or before',
      expected: claimDay,
      actual: lossDay,
    });
  }

  if (lossDay) {
    // 2. Term window, both ends inclusive
    if (lossDay < termStartDay) {
      add({
        step: Step.TERM_WINDOW,
        code: Code.LOSS_BEFORE_TERM_START,
        outcome: Outcome.INELIGIBLE,
        message: 'The loss happened before the warranty started',
        path: 'lossDate',
        operator: 'on or after',
        expected: termStartDay,
        actual: lossDay,
      });
    }

    if (expiryDay && lossDay > expiryDay) {
      add({
        step: Step.TERM_WINDOW,
        code: Code.LOSS_AFTER_EXPIRY,
        outcome: Outcome.INELIGIBLE,
        message: 'The loss happened after the warranty expired',
        path: 'lossDate',
        operator: 'on or before',
        expected: expiryDay,
        actual: lossDay,
      });
    }

    // Cancellation (status step): a loss on the cancellation day is still covered, like claim opening today
    if (cancellationDay && lossDay > cancellationDay) {
      add({
        step: Step.STATUS,
        code: Code.LOSS_AFTER_CANCELLATION,
        outcome: Outcome.INELIGIBLE,
        message: 'The loss happened after the warranty was cancelled',
        path: 'lossDate',
        operator: 'on or before',
        expected: cancellationDay,
        actual: lossDay,
      });
    }
  }

  // 3. Usage limit for the term: ends coverage like expiry, so it's ranked right after the term window
  const { usage, findings: usageFindings } = usageLimitCheck(ctx.plan?.termUsageLimits?.[ctx.warranty.termId], ctx);
  usageFindings.forEach(add);

  // 4. Status, the same ones that block opening a claim
  const statusCodes: Partial<Record<Warranty.Status, Code>> = {
    [Warranty.Status.DRAFT]: Code.WARRANTY_DRAFT,
    [Warranty.Status.VOID]: Code.WARRANTY_VOID,
  };
  const statusCode = statusCodes[ctx.warranty.status];
  if (statusCode) {
    add({
      step: Step.STATUS,
      code: statusCode,
      outcome: Outcome.INELIGIBLE,
      message: `The warranty is ${ctx.warranty.status.toLowerCase()}`,
      path: 'warranty.status',
      actual: ctx.warranty.status,
    });
  }
  if (ctx.warranty.frozen) {
    add({
      step: Step.STATUS,
      code: Code.WARRANTY_FROZEN,
      outcome: Outcome.INELIGIBLE,
      message: 'The warranty is frozen',
      path: 'warranty.frozen',
      actual: true,
    });
  }

  // 5. Waiting period against the loss date; renewed warranties skip it, like claim opening today
  const waitingDays = ctx.policy.claimWaitingPeriod ?? 0;
  if (lossDay && ctx.warranty.renewalCount === 0 && waitingDays > 0) {
    const waitingEnds = addDays(termStartDay, waitingDays);
    if (lossDay < waitingEnds) {
      add({
        step: Step.WAITING_PERIOD,
        code: Code.LOSS_IN_WAITING_PERIOD,
        outcome: Outcome.INELIGIBLE,
        message: `The loss happened during the ${waitingDays}-day waiting period`,
        path: 'lossDate',
        operator: 'on or after',
        expected: waitingEnds,
        actual: lossDay,
      });
    }
  }

  // 6. Coverage: on this warranty, in the plan for this term, and its requirement still applies
  const warrantyCoverage = ctx.coverageId
    ? ctx.warranty.coverages?.find(coverage => coverage.coverageId === ctx.coverageId)
    : undefined;
  const coverageConfig = ctx.coverageId
    ? ctx.plan?.coverageConfigs?.[ctx.warranty.termId]?.find(config => config.coverageId === ctx.coverageId)
    : undefined;

  if (!ctx.coverageId) {
    add({
      step: Step.COVERAGE,
      code: Code.COVERAGE_MISSING,
      outcome: Outcome.EXCLUDED,
      message: 'The job has no coverage selected',
      path: 'coverageId',
    });
  } else if (!warrantyCoverage) {
    add({
      step: Step.COVERAGE,
      code: Code.COVERAGE_NOT_ON_WARRANTY,
      outcome: Outcome.EXCLUDED,
      message: "The coverage isn't on this warranty",
      path: 'coverageId',
      actual: ctx.coverageId,
    });
  } else if (!coverageConfig) {
    add({
      step: Step.COVERAGE,
      code: Code.COVERAGE_NOT_IN_PLAN,
      outcome: Outcome.EXCLUDED,
      message: "The coverage isn't configured on the warranty's plan and term",
      path: 'coverageId',
      actual: ctx.coverageId,
    });
  } else if (warrantyCoverage.requirement === Policy.Plan.CoverageRequirementValue.NOT_APPLICABLE) {
    add({
      step: Step.COVERAGE,
      code: Code.COVERAGE_NOT_APPLICABLE,
      outcome: Outcome.EXCLUDED,
      message: "The coverage wasn't included when the warranty was sold",
      path: 'coverageId',
      expected: 'applicable',
      actual: warrantyCoverage.requirement,
    });
  } else {
    const activeUntil = toDate(warrantyCoverage.activeUntil);
    if (lossDay && isValid(activeUntil) && lossDay > toCalendarDay(activeUntil, tz)) {
      add({
        step: Step.COVERAGE,
        code: Code.COVERAGE_ENDED,
        outcome: Outcome.EXCLUDED,
        message: 'The coverage had ended before the loss',
        path: 'coverageId',
        operator: 'on or before',
        expected: toCalendarDay(activeUntil, tz),
        actual: lossDay,
      });
    }

    requirementFindings(coverageConfig.requirement, ctx.propertyValues).forEach(add);
  }

  // 7. Asset: the stored submission, for this asset type, with its required values and plan conditions
  if (!ctx.submission) {
    add({
      step: Step.ASSET,
      code: Code.ASSET_SUBMISSION_MISSING,
      outcome: Outcome.INSUFFICIENT_INFORMATION,
      message: 'The warranty has no asset details',
      path: 'propertySetSubmissionId',
    });
  } else {
    if (ctx.submission.setId !== ctx.policy.propertySetId) {
      add({
        step: Step.ASSET,
        code: Code.ASSET_SET_MISMATCH,
        outcome: Outcome.INSUFFICIENT_INFORMATION,
        message: "The asset details don't belong to the policy's asset type",
        path: 'propertySetId',
        expected: ctx.policy.propertySetId,
        actual: ctx.submission.setId,
      });
    }

    if (ctx.propertySet) {
      PropertySet.getMissingRequiredFields(
        ctx.propertySet,
        ctx.submission.value ?? {},
        ctx.submission.dimensionValues ?? {}
      ).forEach(field =>
        add({
          step: Step.ASSET,
          code: Code.ASSET_VALUE_MISSING,
          outcome: Outcome.INSUFFICIENT_INFORMATION,
          message: `${field.parentName ? `${field.parentName} › ` : ''}${field.name} is required`,
          path: field.kind === 'DIMENSION' ? `dimension.${field.id}` : field.id,
          label: field.name,
        })
      );
    }

    conditionGroupFindings(
      ctx.plan?.conditions,
      ctx.propertyValues,
      Code.PLAN_CONDITION_FAILED,
      'plan conditions'
    ).forEach(add);
    conditionGroupFindings(
      ctx.plan?.termConditions?.[ctx.warranty.termId],
      ctx.propertyValues,
      Code.TERM_CONDITION_FAILED,
      'term conditions'
    ).forEach(add);
  }

  // 8. Limits, using the existing liability calculation as is
  const limits = warrantyCoverage ? limitsOf(ctx) : null;
  if (limits && limits.requestedAmount > limits.remainingLimit) {
    add({
      step: Step.LIMITS,
      code: Code.LIMIT_BELOW_REQUESTED,
      outcome: Outcome.LIMITED,
      message:
        limits.bindingLimit === 'GROUP'
          ? 'A liability group limit is below the requested amount'
          : 'The coverage limit left is below the requested amount',
      path: limits.bindingLimit === 'GROUP' ? 'limits.groups' : 'limits.remainingCoverageLimit',
      operator: 'at most',
      expected: limits.remainingLimit,
      actual: limits.requestedAmount,
    });
  }

  const sorted = sortFindings(findings);

  return {
    outcome: sorted[0]?.outcome ?? Outcome.ELIGIBLE,
    rulesVersion: RULES_VERSION,
    findings: sorted,
    usage,
    limits,
    inputs: {
      timeZone: tz,
      lossDate: lossDay,
      claimDate: claimDay,
      termStart: termStartDay,
      expiryDate: expiryDay,
      cancellationDate: cancellationDay,
      warrantyStatus: ctx.warranty.status,
      frozen: ctx.warranty.frozen,
      renewalCount: ctx.warranty.renewalCount,
      claimWaitingPeriod: ctx.policy.claimWaitingPeriod,
      policyId: ctx.warranty.policyId,
      planId: ctx.warranty.planId,
      termId: ctx.warranty.termId,
      propertySetId: ctx.policy.propertySetId,
      coverageId: ctx.coverageId,
      usageAtLoss: ctx.usageAtLoss,
      submissionId: ctx.submission?.id ?? null,
      submissionSetId: ctx.submission?.setId ?? null,
      assetValues: mergeDimensionValues(
        normalizePropertyValues(ctx.submission?.value ?? {}, ctx.assetValuesAsOfYear),
        ctx.submission?.dimensionValues
      ),
      assetValuesAsOfYear: ctx.assetValuesAsOfYear,
      requestedAmount: ctx.requestedAmount,
    },
  };
}

// A missing or unmeasurable usage reading waits behind the definite answers, like a missing asset value.
function rankOf(finding: Finding): number {
  if (finding.step === Step.USAGE && finding.outcome === Outcome.INSUFFICIENT_INFORMATION) {
    return STEP_ORDER.indexOf(Step.ASSET) + 0.5;
  }
  return STEP_ORDER.indexOf(finding.step);
}

/** Step order first; inside a step, a definite answer beats missing information. */
export function sortFindings(findings: Finding[]): Finding[] {
  return findings
    .map((finding, index) => ({ finding, index }))
    .sort(
      (a, b) =>
        rankOf(a.finding) - rankOf(b.finding) ||
        OUTCOME_RANK.indexOf(a.finding.outcome) - OUTCOME_RANK.indexOf(b.finding.outcome) ||
        a.index - b.index
    )
    .map(({ finding }) => finding);
}
